import { useEffect, useMemo } from "react";
import type { OrderItem } from "@/api/SolarCloudApi";
import {
  useOrderUuid,
  useSetOrderUuid,
} from "@/stores/orderIdStore";
import { useOrderOrCreate } from "./hooks/useOrderOrCreate";
import { useSearchParamUpdates } from "./hooks/useSearchParamUpdates";
import { useCatalog } from "@/queries/useCatalog";
import LoadingSection from "@/components/LoadingSection";
import ConfigureSolarSystem from "./ConfigureSolarSystem";
import ConfigureSolarSystem1 from "./ConfigureSolarSystem1";
import OrderSummaryCard from "./OrderSummaryCard";
import { isVirtualBatteryIncludedPath, isBuySolarPath } from "../route";
import { useLocation } from "react-router";
import { useBuyPanelsStore } from "../store";
import { useUpdateNewOrder } from "@/queries/order/useUpdateNewOrder";
import { calculateOrderValues } from "@/util/order";
import BuyPanelsVariantHero, {
  type BuyPanelsLandingVariant,
} from "../components/BuyPanelsVariantHero";
import { getBuyPanelsRootSlug } from "../route";

export default function BuyPanels() {
  const setOrderUuid = useSetOrderUuid();
  const orderUuid = useOrderUuid();
  const location = useLocation();
  const resetPaymentFrequency = useBuyPanelsStore(
    (state) => state.resetPaymentFrequency
  );

  const { data: catalog } = useCatalog();
  const { order } = useOrderOrCreate({ orderUuid });
  const { mutate: updateOrder, isPending: isRemovingUnavailableProducts } =
    useUpdateNewOrder({ refetchQueries: false });

  const unavailableItems = useMemo(
    () =>
      order?.items?.filter((item: OrderItem) => {
        const catalogProduct = catalog?.find(
          (product) => product.uuid === item.product?.uuid
        );
        return !catalogProduct || catalogProduct.enabled !== true;
      }) ?? [],
    [catalog, order?.items]
  );

  useEffect(() => {
    if (
      !order ||
      !catalog ||
      unavailableItems.length === 0 ||
      isRemovingUnavailableProducts
    ) return;

    const unavailableProductUuids = new Set(
      unavailableItems.map((item: OrderItem) => item.product?.uuid)
    );
    const updatedOrder = {
      ...order,
      items: (order.items ?? []).filter(
        (item: OrderItem) => !unavailableProductUuids.has(item.product?.uuid)
      ),
    };
    const discountPercentage =
      (order.fullPrice ?? 0) > 0
        ? ((order.totalDiscount ?? 0) / order.fullPrice!) * 100
        : 0;

    updateOrder({
      ...updatedOrder,
      ...calculateOrderValues(updatedOrder, discountPercentage),
    });
  }, [catalog, order, unavailableItems, isRemovingUnavailableProducts, updateOrder]);

  const { hasSearchParams } = useSearchParamUpdates({
    order,
    enabled: !!order && !!catalog,
  });

  useEffect(() => {
    if (order?.uuid) {
      if (order.uuid !== orderUuid) {
        setOrderUuid(order.uuid);
      }
    }
  }, [setOrderUuid, order, orderUuid]);

  useEffect(() => {
    if (!isBuySolarPath(location.pathname)) {
      resetPaymentFrequency();
    }
  }, [location.pathname, resetPaymentFrequency]);

  if (
    !order ||
    !catalog ||
    hasSearchParams ||
    unavailableItems.length > 0 ||
    isRemovingUnavailableProducts
  ) {
    return <LoadingSection />;
  }

  const isVirtualBatteryIncluded = isVirtualBatteryIncludedPath();
  const isVariant1 = isBuySolarPath();
  const rootSlug = getBuyPanelsRootSlug(location.pathname);
  const landingVariant: BuyPanelsLandingVariant | undefined =
    rootSlug === "evsolar" || rootSlug === "corporatesolar"
      ? rootSlug
      : undefined;

  return (
    <div className="w-full">
      {/* Two Column Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Left Column - Configure System */}
        <div className="lg:col-span-2">
          {landingVariant && <BuyPanelsVariantHero variant={landingVariant} />}
          {isVariant1 ? (
            <ConfigureSolarSystem1 isVirtualBatteryIncluded={isVirtualBatteryIncluded} />
          ) : (
            <ConfigureSolarSystem
              isVirtualBatteryIncluded={isVirtualBatteryIncluded}
              hideDefaultIntro={!!landingVariant}
            />
          )}
          {/* <AveragePanelsFooter /> */}
        </div>

        {/* Right Column - Order Summary */}
        <div className="lg:col-span-1">
          <OrderSummaryCard />
        </div>
      </div>
    </div>
  );
}
